import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import * as AvatarPrimitive from "@radix-ui/react-avatar";

import { AvatarImage } from "./avatar";

type AvatarLoadingStatusType = "idle" | "loading" | "loaded" | "error";

const MAX_RETRIES = 1;
const RETRY_DELAY_MS = 500;

interface RetryInfoType {
  readonly url: string;
  readonly forSrc: string;
}

function PlantaAvatarImage({
  src,
  onLoadingStatusChange,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>): React.JSX.Element {
  const [retryInfo, setRetryInfo] = useState<RetryInfoType | null>(null);
  const retryCountRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    retryCountRef.current = 0;
    return (): void => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [src]);

  const handleLoadingStatusChange = useCallback(
    (status: AvatarLoadingStatusType): void => {
      if (status === "error" && src !== undefined) {
        if (retryCountRef.current >= MAX_RETRIES) {
          console.warn("[PlantaAvatarImage] Retry exhausted for:", src);
          onLoadingStatusChange?.(status);
          return;
        }
        retryCountRef.current += 1;
        console.warn("[PlantaAvatarImage] Retrying image load for:", src);
        timerRef.current = setTimeout(() => {
          const separator = src.includes("?") ? "&" : "?";
          setRetryInfo({
            url: `${src}${separator}_r=${Date.now()}`,
            forSrc: src,
          });
        }, RETRY_DELAY_MS);
        return; // Don't forward error while retry is pending
      }
      onLoadingStatusChange?.(status);
    },
    [src, onLoadingStatusChange],
  );

  const effectiveSrc =
    retryInfo !== null && retryInfo.forSrc === src ? retryInfo.url : src;

  return (
    <AvatarImage
      src={effectiveSrc}
      onLoadingStatusChange={handleLoadingStatusChange}
      {...props}
    />
  );
}

export { PlantaAvatarImage };
